import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  LinearProgress,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  Stack,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import DownloadDoneIcon from '@mui/icons-material/DownloadDone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { API_URL } from '../config';
import { fetchApi } from '../utils/apiUtils';

interface ScrapeResults {
  scrapedDrivers?: number;
  totalScraped?: number;
  driversUpdated?: number;
  totalUpdated?: number;
  driversNotFound?: number;
  totalNotFound?: number;
  errors?: string[];
  summary?: string;
  page?: string;
  processingTime?: number;
}

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const sectionTitleSx = {
  letterSpacing: '0.04em',
  fontWeight: 500,
  color: 'text.secondary',
  display: 'block',
  mb: 1.5,
};

function DriverLocationScraper(): React.ReactElement {
  const [selectedPage, setSelectedPage] = useState<string>('A');
  const [loading, setLoading] = useState(false);
  const [fullScrapeLoading, setFullScrapeLoading] = useState(false);
  const [results, setResults] = useState<ScrapeResults | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSinglePageScrape = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    setResults(null);
    try {
      const data = (await fetchApi(`${API_URL}/myraceplass/scrape-driver-directory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ page: selectedPage }),
      })) as ScrapeResults;
      setResults(data);
    } catch (err) {
      console.error('Single page scrape error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to scrape page ${selectedPage}: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFullScrape = async (): Promise<void> => {
    setFullScrapeLoading(true);
    setError(null);
    setResults(null);
    try {
      const data = (await fetchApi(`${API_URL}/myraceplass/scrape-all-driver-directory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })) as ScrapeResults;
      setResults(data);
    } catch (err) {
      console.error('Full scrape error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to scrape all pages: ${errorMessage}`);
    } finally {
      setFullScrapeLoading(false);
    }
  };

  const handlePageChange = (e: SelectChangeEvent): void => {
    setSelectedPage(e.target.value);
  };

  const stats = useMemo(() => {
    if (!results) return [];
    const scraped = results.scrapedDrivers ?? results.totalScraped ?? 0;
    const updated = results.driversUpdated ?? results.totalUpdated ?? 0;
    const notFound = results.driversNotFound ?? results.totalNotFound ?? 0;
    const errorCount = results.errors?.length ?? 0;
    return [
      { label: 'Scraped', value: scraped.toLocaleString(), icon: <DownloadDoneIcon /> },
      {
        label: 'Updated',
        value: updated.toLocaleString(),
        icon: <CheckCircleIcon />,
        tone: 'success' as const,
      },
      { label: 'Not Found', value: notFound.toLocaleString(), icon: <HelpOutlineIcon /> },
      {
        label: 'Errors',
        value: errorCount.toLocaleString(),
        icon: <ReportProblemOutlinedIcon />,
        tone: errorCount > 0 ? ('error' as const) : undefined,
      },
    ];
  }, [results]);

  const isBusy = loading || fullScrapeLoading;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          Driver Location Scraper
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Pull hometown and state from the MyRacePass driver directory and update matching drivers
          here.
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="overline" sx={sectionTitleSx}>
              Single Page
            </Typography>
            <Stack spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Page (last name initial)</InputLabel>
                <Select
                  value={selectedPage}
                  label="Page (last name initial)"
                  onChange={handlePageChange}
                  disabled={isBusy}
                >
                  {ALPHABET.map((letter) => (
                    <MenuItem key={letter} value={letter}>
                      {letter}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={handleSinglePageScrape}
                disabled={isBusy}
                startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
              >
                {loading ? 'Scraping' : `Scrape page ${selectedPage}`}
              </Button>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="overline" sx={sectionTitleSx}>
              Full Directory
            </Typography>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Walk all 26 pages (A–Z). Takes a few minutes.
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleFullScrape}
                disabled={isBusy}
                startIcon={fullScrapeLoading ? <CircularProgress size={14} color="inherit" /> : null}
              >
                {fullScrapeLoading ? 'Scraping all pages' : 'Scrape A–Z'}
              </Button>
              {fullScrapeLoading && (
                <Box>
                  <LinearProgress />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Roughly 2–3 minutes.
                  </Typography>
                </Box>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {results && (
        <Paper sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="overline" sx={sectionTitleSx}>
            Scrape Results
          </Typography>
          <Grid container spacing={2} sx={{ mb: results.summary || results.errors?.length ? 2 : 0 }}>
            {stats.map((stat) => (
              <Grid key={stat.label} item xs={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                      <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{ letterSpacing: '0.04em', fontWeight: 500, lineHeight: 1.2 }}
                      >
                        {stat.label}
                      </Typography>
                      <Box
                        sx={{
                          color:
                            stat.tone === 'success'
                              ? 'success.main'
                              : stat.tone === 'error'
                                ? 'error.main'
                                : 'text.disabled',
                          display: 'flex',
                          alignItems: 'center',
                          '& svg': { fontSize: 18 },
                        }}
                      >
                        {stat.icon}
                      </Box>
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}
                    >
                      {stat.value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {(results.page || results.processingTime || results.summary) && (
            <Stack
              direction="row"
              spacing={2}
              flexWrap="wrap"
              useFlexGap
              sx={{ color: 'text.secondary', fontSize: '0.8125rem', mb: results.summary ? 1.5 : 0 }}
            >
              {results.page && (
                <Box>
                  <Typography component="span" variant="caption" sx={{ fontWeight: 500 }}>
                    Page:
                  </Typography>{' '}
                  {results.page}
                </Box>
              )}
              {results.processingTime !== undefined && (
                <Box>
                  <Typography component="span" variant="caption" sx={{ fontWeight: 500 }}>
                    Time:
                  </Typography>{' '}
                  {Math.round(results.processingTime / 1000)}s
                </Box>
              )}
            </Stack>
          )}

          {results.summary && (
            <Alert severity="success" sx={{ mb: results.errors?.length ? 2 : 0 }}>
              {results.summary}
            </Alert>
          )}

          {results.errors && results.errors.length > 0 && (
            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ letterSpacing: '0.04em', fontWeight: 500, display: 'block', mb: 0.5 }}
              >
                Errors
              </Typography>
              <List dense disablePadding>
                {results.errors.slice(0, 10).map((err, index) => (
                  <ListItem key={index} disableGutters sx={{ py: 0.25 }}>
                    <ListItemText
                      primary={err}
                      primaryTypographyProps={{
                        variant: 'body2',
                        sx: { color: 'error.main', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.8125rem' },
                      }}
                    />
                  </ListItem>
                ))}
                {results.errors.length > 10 && (
                  <ListItem disableGutters sx={{ py: 0.25 }}>
                    <ListItemText
                      primary={`+ ${results.errors.length - 10} more`}
                      primaryTypographyProps={{
                        variant: 'caption',
                        sx: { color: 'text.disabled', fontStyle: 'italic' },
                      }}
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          )}
        </Paper>
      )}

      <Paper sx={{ p: 2.5 }}>
        <Typography variant="overline" sx={sectionTitleSx}>
          How it works
        </Typography>
        <Stack spacing={0.5} sx={{ color: 'text.secondary' }}>
          <Typography variant="body2">
            Pulls names and locations from the MyRacePass driver directory.
          </Typography>
          <Typography variant="body2">
            Matches by first and last name; prioritises drivers missing location data.
          </Typography>
          <Typography variant="body2">
            Preserves existing data when multiple drivers match the same name.
          </Typography>
          <Typography variant="body2">
            Adds a 1-second delay between page requests to be polite to MyRacePass.
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}

export default DriverLocationScraper;
